import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Avatar,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from '@mui/material'
import { format, formatDistanceToNowStrict } from 'date-fns'
import { getProjectScansList } from '../api'
import { ScanEvent } from '../model'
import { EMBED_ANALYTICS_COLORS, SmartphoneIcon } from '../components'
import { translate } from '../i18n'
import { ScansListProps } from './ScansList.types'

const PAGE_SIZE = 20
// Bounded scroll region so the analytics card stays a reasonable size; the list
// scrolls internally and lazily loads more as the sentinel comes into view.
const MAX_HEIGHT = 420

const formatLocation = (event: ScanEvent): string =>
    [event.city, event.state, event.country].filter(Boolean).join(', ')

export default function ScansList({
    projectId,
    filters,
    embedded = false,
}: ScansListProps) {
    const [items, setItems] = useState<ScanEvent[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>()
    const [initialized, setInitialized] = useState(false)

    const scrollRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)

    // Identifies the active filter set. Responses that arrive after the filters
    // change carry a stale key and are discarded, preventing out-of-order pages.
    const queryKey = `${projectId}|${filters.from ?? ''}|${filters.to ?? ''}|${
        filters.platform ?? ''
    }`
    const activeQuery = useRef(queryKey)

    const hasMore = items.length < total

    const loadPage = useCallback(
        async (nextPage: number, key: string) => {
            setLoading(true)
            setError(undefined)
            try {
                const response = await getProjectScansList(projectId, {
                    from: filters.from,
                    to: filters.to,
                    platform: filters.platform,
                    page: nextPage,
                    limit: PAGE_SIZE,
                })
                if (activeQuery.current !== key) {
                    return
                }
                setItems((prev) =>
                    nextPage === 1
                        ? response.items
                        : [...prev, ...response.items]
                )
                setTotal(response.total)
                setPage(nextPage)
            } catch (e) {
                if (activeQuery.current !== key) {
                    return
                }
                setError((e as Error).message)
            } finally {
                if (activeQuery.current === key) {
                    setLoading(false)
                    setInitialized(true)
                }
            }
        },
        [projectId, filters.from, filters.to, filters.platform]
    )

    // Reset and reload from the first page whenever the filter set changes.
    useEffect(() => {
        activeQuery.current = queryKey
        setItems([])
        setTotal(0)
        setPage(1)
        setInitialized(false)
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0
        }
        loadPage(1, queryKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey])

    // Infinite scroll: load the next page when the sentinel scrolls into view.
    useEffect(() => {
        const sentinel = sentinelRef.current
        const root = scrollRef.current
        if (!sentinel || !root) {
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMore &&
                    !loading &&
                    !error
                ) {
                    loadPage(page + 1, activeQuery.current)
                }
            },
            { root, rootMargin: '120px' }
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [hasMore, loading, error, page, loadPage])

    const showInitialSpinner = loading && items.length === 0
    const showEmpty = initialized && !loading && !error && items.length === 0

    return (
        <Box
            ref={scrollRef}
            sx={{
                maxHeight: MAX_HEIGHT,
                overflowY: 'auto',
                pr: 0.5,
            }}
        >
            {items.length > 0 && (
                <List disablePadding>
                    {items.map((event) => {
                        const location = formatLocation(event)
                        const relative = formatDistanceToNowStrict(
                            new Date(event.timestamp)
                        )
                        const primary = location
                            ? (translate(
                                  'projects.analytics.list.scanned_ago_location',
                                  { time: relative, location }
                              ) as string)
                            : (translate(
                                  'projects.analytics.list.scanned_ago',
                                  { time: relative }
                              ) as string)
                        const secondary = event.platform
                            ? (translate(
                                  `projects.analytics.platforms.${event.platform}`
                              ) as string)
                            : undefined
                        const absolute = format(
                            new Date(event.timestamp),
                            'MMM d, yyyy, h:mm a'
                        )
                        return (
                            <ListItem
                                key={event.id}
                                divider
                                alignItems="flex-start"
                                secondaryAction={
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {absolute}
                                    </Typography>
                                }
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            bgcolor: embedded
                                                ? EMBED_ANALYTICS_COLORS.data
                                                : 'action.selected',
                                            color: embedded
                                                ? EMBED_ANALYTICS_COLORS.onData
                                                : 'text.secondary',
                                        }}
                                    >
                                        <SmartphoneIcon fontSize="small" />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={primary}
                                    secondary={secondary}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                    }}
                                />
                            </ListItem>
                        )
                    })}
                </List>
            )}

            {showInitialSpinner && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 4,
                    }}
                >
                    <CircularProgress size={28} />
                </Box>
            )}

            {loading && items.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 2,
                    }}
                >
                    <CircularProgress size={20} />
                </Box>
            )}

            {error && !loading && (
                <Typography
                    variant="body2"
                    align="center"
                    color="error"
                    sx={{ py: 3 }}
                >
                    {error}
                </Typography>
            )}

            {showEmpty && (
                <Typography
                    variant="body2"
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                >
                    {translate('projects.analytics.list.empty')}
                </Typography>
            )}

            <Box ref={sentinelRef} sx={{ height: 1 }} />
        </Box>
    )
}
